/**
 * EarlyWarningsPage — Early Warnings & Safe Rotations dashboard.
 *
 * Surfaces a 4-layer scoring stack:
 *   1) Market regime banner (RISK_ON / NEUTRAL / RISK_OFF) from SPY/QQQ/IWM
 *   2) Subsector group health: ranked Deteriorating / Recovering / Breaking-out
 *   3) Stock screens: dropping / recovering / breaking-out (top 30 each)
 *   4) Per-ticker drill-down with all triggered booleans + Claude explanation
 *
 * Plus a Breakout Scanner add-on and a 5-year EOD backfill button so signals
 * can be replayed historically.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  AlertTriangle, TrendingUp, TrendingDown, Sparkles, RefreshCw,
  Activity, Lightbulb, ChevronDown, ChevronRight, X, Zap, Shield,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "../config/api";

const fmtPct = (v, d = 2) => v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(d)}%`;
const fmtPrice = (v) => v === null || v === undefined ? "—" : `$${Number(v).toFixed(2)}`;
const pctClass = (v) => v == null ? "text-slate-400" : v >= 0 ? "text-emerald-300" : "text-rose-300";
const scoreClass = (v, kind = "warn") => {
  if (v == null) return "text-slate-400";
  if (kind === "warn") return v >= 60 ? "text-rose-300" : v >= 40 ? "text-amber-300" : "text-slate-300";
  if (kind === "good") return v >= 60 ? "text-emerald-300" : v >= 40 ? "text-cyan-300" : "text-slate-300";
  return "text-slate-300";
};

// ───────────────────────── Regime banner ─────────────────────────
function RegimeBanner({ regime, loading }) {
  if (loading) {
    return <Skeleton className="h-28 bg-slate-900/40 mb-6" data-testid="ew-regime-skel" />;
  }
  if (!regime || !regime.label) return null;
  const label = regime.label;
  const themes = {
    RISK_ON:   { bg: "from-emerald-900/40 to-emerald-800/10", border: "border-emerald-700", text: "text-emerald-200", Icon: Shield, sub: "Trend supports adding risk. Buy-setup signals dominate." },
    NEUTRAL:   { bg: "from-amber-900/30 to-amber-800/10",     border: "border-amber-700",   text: "text-amber-200",   Icon: Activity, sub: "Mixed signals. Lean into best-of-breed setups, not the index." },
    RISK_OFF:  { bg: "from-rose-900/40 to-rose-800/10",       border: "border-rose-700",    text: "text-rose-200",    Icon: AlertTriangle, sub: "Deterioration signals dominate. Tighten stops + raise cash." },
    UNKNOWN:   { bg: "from-slate-900/40 to-slate-800/10",     border: "border-slate-700",   text: "text-slate-300",   Icon: Activity, sub: "Insufficient data." },
  };
  const t = themes[label] || themes.UNKNOWN;
  const Icon = t.Icon;
  return (
    <Card className={`bg-gradient-to-br ${t.bg} ${t.border} border-2 mb-6`} data-testid="ew-regime-banner">
      <CardContent className="py-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <Icon className={`w-7 h-7 ${t.text}`} />
              <div>
                <div className={`text-3xl font-black tracking-tight ${t.text}`} data-testid="ew-regime-label">
                  {label.replace("_", " ")}
                </div>
                <div className="text-xs text-slate-200 mt-0.5">{t.sub}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-4 text-xs">
            <Kpi label="Net score"     value={fmtPct(regime.score)}                  cls={pctClass(regime.score)} testid="ew-regime-net" />
            <Kpi label="Avg recovery"  value={`${regime.avg_recovery_pct ?? "—"}%`}  cls="text-emerald-300" />
            <Kpi label="Avg deterioration" value={`${regime.avg_deterioration_pct ?? "—"}%`} cls="text-rose-300" />
            <Kpi label="Avg breakout"  value={`${regime.avg_breakout_pct ?? "—"}%`}  cls="text-amber-300" />
          </div>
        </div>
        {regime.long_term_downtrend && (
          <div className="mt-3 px-3 py-1.5 rounded bg-rose-900/30 border border-rose-700/50 text-[11px] text-rose-200 inline-flex items-center gap-1.5">
            <AlertTriangle className="w-3 h-3" />
            Long-term downtrend on SPY (SMA50 &lt; SMA200) — be selective on longs.
          </div>
        )}
        <table className="w-full text-xs mt-3" data-testid="ew-regime-table">
          <thead className="text-slate-200 uppercase text-[10px] font-bold">
            <tr>
              <th className="text-left py-1">Index</th>
              <th className="text-right">Last</th>
              <th className="text-right">1D</th>
              <th className="text-right">20D</th>
              <th className="text-right">Det %</th>
              <th className="text-right">Rec %</th>
              <th className="text-right">Bk %</th>
              <th className="text-right">Net %</th>
            </tr>
          </thead>
          <tbody>
            {(regime.details || []).map(r => (
              <tr key={r.symbol} className="border-t border-slate-700/60 hover:bg-slate-900/30">
                <td className="py-1 font-mono">
                  <a href={`/stock?ticker=${r.symbol}`} target="_blank" rel="noopener noreferrer"
                     className="text-cyan-300 hover:underline hover:text-cyan-200 font-bold">
                    {r.symbol}
                  </a>
                </td>
                <td className="text-right font-mono text-slate-100">{fmtPrice(r.last)}</td>
                <td className={`text-right font-mono ${pctClass(r.r1d)}`}>{fmtPct(r.r1d)}</td>
                <td className={`text-right font-mono ${pctClass(r.r20d)}`}>{fmtPct(r.r20d)}</td>
                <td className={`text-right font-mono ${scoreClass(r.deterioration_pct, "warn")}`}>{r.deterioration_pct}%</td>
                <td className={`text-right font-mono ${scoreClass(r.recovery_pct, "good")}`}>{r.recovery_pct}%</td>
                <td className="text-right font-mono text-amber-300">{r.breakout_pct}%</td>
                <td className={`text-right font-mono font-bold ${pctClass(r.net_pct)}`}>{fmtPct(r.net_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function Kpi({ label, value, cls, testid }) {
  return (
    <div className="min-w-[80px]" data-testid={testid}>
      <div className="text-[10px] uppercase text-slate-200 font-semibold tracking-wider">{label}</div>
      <div className={`text-lg font-mono font-bold ${cls}`}>{value}</div>
    </div>
  );
}

// ───────────────────────── Subsector tables ─────────────────────────
function SubsectorSection({ data, loading, onPick }) {
  if (loading) return <Skeleton className="h-72 bg-slate-900/40" data-testid="ew-sub-skel" />;
  if (!data) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6" data-testid="ew-subsector-grid">
      <SubsectorTable
        title="Deteriorating"
        rows={data.deteriorating || []}
        valueKey="deterioration_pct"
        countKey="deterioration_count"
        Icon={TrendingDown}
        accent="rose"
        onPick={onPick}
        testid="ew-deteriorating"
      />
      <SubsectorTable
        title="Recovering"
        rows={data.recovering || []}
        valueKey="recovery_pct"
        countKey="recovery_count"
        Icon={TrendingUp}
        accent="emerald"
        onPick={onPick}
        testid="ew-recovering"
      />
      <SubsectorTable
        title="Breaking out"
        rows={data.breaking_out || []}
        valueKey="breakout_pct"
        countKey="breakout_count"
        Icon={Zap}
        accent="amber"
        onPick={onPick}
        testid="ew-breakout"
      />
    </div>
  );
}

function SubsectorTable({ title, rows, valueKey, countKey, Icon, accent, onPick, testid }) {
  const accentMap = {
    rose:    { bar: "bg-rose-500",    text: "text-rose-300",    border: "border-rose-800/50" },
    emerald: { bar: "bg-emerald-500", text: "text-emerald-300", border: "border-emerald-800/50" },
    amber:   { bar: "bg-amber-500",   text: "text-amber-300",   border: "border-amber-800/50" },
  };
  const t = accentMap[accent];
  return (
    <Card className={`bg-slate-900/40 border-slate-800 ${t.border} border-l-4`} data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-white">
          <Icon className={`w-4 h-4 ${t.text}`} />
          {title}
          <span className="text-[10px] text-slate-300 font-normal ml-auto">{rows.length} signals</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {rows.length === 0 ? (
          <div className="text-[11px] text-slate-300 italic py-4 text-center">No signals trigger today.</div>
        ) : (
          <div className="space-y-1">
            {rows.slice(0, 12).map(r => (
              <div
                key={r.symbol}
                data-testid={`${testid}-row-${r.symbol}`}
                className="w-full grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-2 py-1.5 rounded hover:bg-slate-800/60 transition-colors"
              >
                <a
                  href={`/stock?ticker=${r.symbol}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`${testid}-link-${r.symbol}`}
                  className="font-mono font-bold text-cyan-300 hover:underline hover:text-cyan-200 text-xs w-12"
                >
                  {r.symbol}
                </a>
                <button
                  onClick={() => onPick?.(r.symbol)}
                  className="text-left text-[11px] text-slate-200 truncate hover:text-white"
                >
                  {r.label || r.parent_etf}
                </button>
                <span className={`font-mono text-[10px] ${pctClass(r.r5d)}`}>{fmtPct(r.r5d, 1)}</span>
                <button
                  onClick={() => onPick?.(r.symbol)}
                  className={`font-mono text-xs font-bold ${t.text} w-16 text-right hover:brightness-125`}
                >
                  {r[valueKey]}%
                  <span className="block text-[9px] text-slate-300 font-normal">{r[countKey]}</span>
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ───────────────────────── Stock screens ─────────────────────────
function StockScreensSection({ data, loading, onPick, etfFilter, setEtfFilter, etfOptions }) {
  if (loading) return <Skeleton className="h-72 bg-slate-900/40" data-testid="ew-stocks-skel" />;
  if (!data) return null;
  return (
    <Card className="bg-slate-900/40 border-slate-800 mb-6" data-testid="ew-stocks-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap text-white">
          <Activity className="w-4 h-4 text-cyan-400" />
          Stock-level screens
          <span className="text-[10px] text-slate-300 font-normal">
            {data.universe_size || 0} stocks scored
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[10px] uppercase text-slate-200 font-semibold">Parent ETF:</span>
            <select
              value={etfFilter || ""}
              onChange={(e) => setEtfFilter(e.target.value || null)}
              data-testid="ew-stocks-etf-filter"
              className="bg-slate-900 border border-slate-600 rounded px-2 py-0.5 text-[11px] text-slate-100 font-mono"
            >
              <option value="">ALL</option>
              {(etfOptions || []).map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 grid grid-cols-1 lg:grid-cols-3 gap-3">
        <StockTable title="Dropping"     rows={data.dropping || []}     valueKey="stock_drop_pct" accent="rose"    Icon={TrendingDown} onPick={onPick} testid="ew-stocks-drop" />
        <StockTable title="Recovering"   rows={data.recovering || []}   valueKey="recovery_pct"   accent="emerald" Icon={TrendingUp}   onPick={onPick} testid="ew-stocks-rec" />
        <StockTable title="Breaking out" rows={data.breaking_out || []} valueKey="breakout_pct"   accent="amber"   Icon={Zap}          onPick={onPick} testid="ew-stocks-bk" />
      </CardContent>
    </Card>
  );
}

function StockTable({ title, rows, valueKey, accent, Icon, onPick, testid }) {
  const accentMap = {
    rose:    "text-rose-300",
    emerald: "text-emerald-300",
    amber:   "text-amber-300",
  };
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-white">
        <Icon className={`w-3.5 h-3.5 ${accentMap[accent]}`} />
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-300 italic py-2">No setups.</div>
      ) : (
        <div className="space-y-0.5">
          {rows.slice(0, 15).map(r => (
            <div
              key={r.symbol}
              data-testid={`${testid}-row-${r.symbol}`}
              className="w-full grid grid-cols-[auto_auto_1fr_auto] gap-2 items-center px-2 py-1 rounded hover:bg-slate-800/60 transition-colors text-xs"
            >
              <a
                href={`/stock?ticker=${r.symbol}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testid}-link-${r.symbol}`}
                className="font-mono font-bold text-cyan-300 hover:underline hover:text-cyan-200 w-12"
              >
                {r.symbol}
              </a>
              <a
                href={`/stock?ticker=${r.parent_etf}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[10px] text-slate-300 hover:underline hover:text-slate-100 w-9"
              >
                {r.parent_etf}
              </a>
              <button
                onClick={() => onPick?.(r.symbol)}
                className={`text-right font-mono text-[10px] ${pctClass(r.r5d)} hover:brightness-125`}
              >
                {fmtPct(r.r5d, 1)}
              </button>
              <button
                onClick={() => onPick?.(r.symbol)}
                className={`font-mono font-bold ${accentMap[accent]} w-12 text-right hover:brightness-125`}
              >
                {r[valueKey]}%
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── Drill-down panel ─────────────────────────
function StockDrillPanel({ ticker, onClose }) {
  const [data, setData] = useState(null);
  const [explanation, setExplanation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expLoading, setExpLoading] = useState(false);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true); setData(null); setExplanation(null);
    axios.get(`${API}/early-warnings/stock/${ticker}`, { timeout: 30000 })
      .then(r => setData(r.data))
      .catch(() => setData({ error: "Failed to load" }))
      .finally(() => setLoading(false));
  }, [ticker]);

  const getExplain = async () => {
    setExpLoading(true);
    try {
      const r = await axios.post(`${API}/early-warnings/explain`,
        { symbol: ticker, kind: "auto" }, { timeout: 30000 });
      setExplanation(r.data);
    } catch (e) {
      setExplanation({ explanation: e?.message || "Failed to generate explanation" });
    }
    setExpLoading(false);
  };

  if (!ticker) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
         data-testid="ew-drill-panel"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="bg-slate-900 border-slate-700 w-full max-w-4xl mt-12 mb-12">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-white">
            <Activity className="w-4 h-4 text-cyan-400" />
            <a href={`/stock?ticker=${ticker}`} target="_blank" rel="noopener noreferrer"
               className="font-mono text-cyan-300 font-bold hover:underline hover:text-cyan-200">
              {ticker}
            </a>
            {data?.parent_etf && (
              <a href={`/stock?ticker=${data.parent_etf}`} target="_blank" rel="noopener noreferrer"
                 className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-200 font-mono hover:bg-slate-700 hover:text-white">
                parent: {data.parent_etf}
              </a>
            )}
          </CardTitle>
          <button onClick={onClose} data-testid="ew-drill-close" className="text-slate-200 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading && <Skeleton className="h-48" />}
          {data?.error && <div className="text-rose-300 text-sm">{data.error}</div>}
          {data?.scores && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                <Kpi label="Last"          value={fmtPrice(data.features?.last_close)}        cls="text-slate-100" />
                <Kpi label="1D"            value={fmtPct(data.features?.r1d)}                  cls={pctClass(data.features?.r1d)} />
                <Kpi label="Deterioration" value={`${data.scores.deterioration_pct}%`}         cls={scoreClass(data.scores.deterioration_pct, "warn")} />
                <Kpi label="Recovery"      value={`${data.scores.recovery_pct}%`}              cls={scoreClass(data.scores.recovery_pct, "good")} />
                <Kpi label="Breakout"      value={`${data.scores.breakout_pct}%`}              cls="text-amber-300" />
              </div>

              <button
                onClick={getExplain}
                disabled={expLoading}
                data-testid="ew-explain-btn"
                className="w-full px-3 py-2 rounded border border-amber-500 bg-amber-600/30 text-white text-sm font-bold hover:bg-amber-600/50 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Lightbulb className="w-4 h-4 text-amber-200" />
                {expLoading ? "Asking Claude…" : explanation ? "Refresh explanation" : "Explain in plain English"}
              </button>

              {explanation && (
                <div className="rounded border border-amber-700/70 bg-amber-900/20 p-3 text-sm text-slate-100 leading-relaxed"
                     data-testid="ew-explain-panel">
                  <div className="text-[10px] uppercase text-amber-200 font-bold tracking-wider mb-1.5">
                    {explanation.kind} signal {explanation.llm_used ? "· Claude" : "· templated"}
                  </div>
                  {explanation.explanation}
                  {explanation.triggered_signals?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {explanation.triggered_signals.slice(0, 8).map(s => (
                        <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-100">
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <FlagGrid title="D1–D18 Deterioration" flags={data.flags?.deterioration} accent="rose" testid="ew-flags-d" />
              <FlagGrid title="R1–R10 Recovery"     flags={data.flags?.recovery}      accent="emerald" testid="ew-flags-r" />
              <FlagGrid title="BK1–BK18 Breakout"   flags={data.flags?.breakout}      accent="amber"   testid="ew-flags-bk" />
              <FlagGrid title="S1–S10 Stock Drop"   flags={data.flags?.stock_drop}    accent="rose"    testid="ew-flags-s" />

              <div className="text-[11px] text-slate-200">
                Suggested 2×ATR stop:
                <span className="ml-1 font-mono text-amber-300 font-bold">
                  {data.features?.atr10 && data.features?.last_close
                    ? `$${(data.features.last_close - 2 * data.features.atr10).toFixed(2)}`
                    : "—"}
                </span>
                {" · "}<span className="text-slate-100">ATR10 {data.features?.atr10?.toFixed(2) ?? "—"}</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FlagGrid({ title, flags, accent, testid }) {
  if (!flags) return null;
  const entries = Object.entries(flags);
  const triggered = entries.filter(([, v]) => v);
  const acc = {
    rose:    "text-rose-200 bg-rose-900/40 border-rose-600",
    emerald: "text-emerald-200 bg-emerald-900/40 border-emerald-600",
    amber:   "text-amber-200 bg-amber-900/40 border-amber-600",
  }[accent];
  return (
    <div data-testid={testid}>
      <div className="text-[12px] font-bold text-white mb-1.5">
        {title}
        <span className="text-[11px] text-slate-200 font-normal ml-1.5">{triggered.length}/{entries.length} triggered</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {entries.map(([k, v]) => (
          <span key={k}
            className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${v ? acc : "text-slate-400 border-slate-700 bg-slate-900/30"}`}>
            {k}
          </span>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────── Backfill panel ─────────────────────────
function BackfillPanel() {
  const [status, setStatus] = useState(null);
  const [years, setYears] = useState(5);
  const [polling, setPolling] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/early-warnings/backfill-status`, { timeout: 10000 });
      setStatus(r.data);
      if (!r.data.running) setPolling(false);
    } catch (_e) { /* status fetch failure is non-fatal */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (!polling) return;
    const id = setInterval(refresh, 4000);
    return () => clearInterval(id);
  }, [polling, refresh]);

  const start = async () => {
    try {
      const r = await axios.post(`${API}/early-warnings/backfill`,
        { years }, { timeout: 15000 });
      setStatus(r.data);
      setPolling(true);
    } catch (e) {
      setStatus({ error: e?.message || "Failed to start" });
    }
  };

  const pct = status?.total_symbols
    ? Math.round((status.completed_symbols / status.total_symbols) * 100)
    : 0;

  return (
    <Card className="bg-slate-900/40 border-slate-800 mb-6" data-testid="ew-backfill-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-white">
          <Sparkles className="w-4 h-4 text-purple-300" />
          5-year EOD backfill <span className="text-[11px] text-slate-200 font-normal">— enables signal backtesting</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-[11px] uppercase text-slate-100 font-bold">Years</label>
            <select value={years} onChange={(e) => setYears(parseInt(e.target.value, 10))}
                    disabled={status?.running}
                    data-testid="ew-backfill-years"
                    className="bg-slate-900 border border-slate-600 rounded px-2 py-0.5 text-xs text-slate-100">
              {[1, 2, 5, 10].map(y => <option key={y} value={y}>{y}y</option>)}
            </select>
          </div>
          <button onClick={start} disabled={status?.running}
                  data-testid="ew-backfill-start"
                  className="px-3 py-1.5 rounded border border-purple-400 bg-purple-600/30 text-white text-xs font-bold hover:bg-purple-600/50 disabled:opacity-50">
            {status?.running ? "Running…" : "Start backfill"}
          </button>
          {status?.running && (
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <div className="flex-1 h-2 rounded bg-slate-700 overflow-hidden">
                <div className="h-full bg-purple-400 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <span className="text-[11px] font-mono text-slate-100 whitespace-nowrap font-semibold">
                {status.completed_symbols}/{status.total_symbols} ({pct}%) · {status.writes} writes
              </span>
            </div>
          )}
        </div>
        {status?.finished_at && !status.running && (
          <div className="text-[11px] text-slate-200 mt-2">
            Last backfill: {status.completed_symbols}/{status.total_symbols} symbols ·
            {status.writes} daily score rows persisted ·
            finished {new Date(status.finished_at).toLocaleString()}
          </div>
        )}
        {status?.error && (
          <div className="text-[11px] text-rose-200 mt-2 font-semibold">{status.error}</div>
        )}
      </CardContent>
    </Card>
  );
}

// ───────────────────────── Page ─────────────────────────
export default function EarlyWarningsPage() {
  const [regime, setRegime] = useState(null);
  const [subsectors, setSubsectors] = useState(null);
  const [stocks, setStocks] = useState(null);
  const [regimeLoading, setRegimeLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [drillTicker, setDrillTicker] = useState(null);
  const [etfFilter, setEtfFilter] = useState(null);
  const [etfOptions, setEtfOptions] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    setRegimeLoading(true); setSubLoading(true); setStocksLoading(true);

    // Kick all three in parallel
    const regimeP = axios.get(`${API}/early-warnings/regime`, { timeout: 60000 })
      .then(r => setRegime(r.data))
      .catch(e => setError(e?.message || "regime failed"))
      .finally(() => setRegimeLoading(false));
    const subsP = axios.get(`${API}/early-warnings/subsectors`, { timeout: 120000 })
      .then(r => setSubsectors(r.data))
      .finally(() => setSubLoading(false));
    const stocksP = axios.get(`${API}/early-warnings/stocks?top_n_per_etf=15`, { timeout: 180000 })
      .then(r => setStocks(r.data))
      .finally(() => setStocksLoading(false));

    await Promise.allSettled([regimeP, subsP, stocksP]);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    axios.get(`${API}/early-warnings/universe`, { timeout: 10000 })
      .then(r => {
        setEtfOptions([
          ...Object.keys(r.data.sector_etfs || {}),
          ...Object.keys(r.data.subsector_etfs || {}),
        ]);
      }).catch(() => {});
    load();
  }, [load]);

  useEffect(() => {
    if (etfFilter === undefined) return;
    setStocksLoading(true);
    const params = etfFilter ? `?parent_etf=${etfFilter}&top_n_per_etf=25` : `?top_n_per_etf=15`;
    axios.get(`${API}/early-warnings/stocks${params}`, { timeout: 180000 })
      .then(r => setStocks(r.data))
      .catch(() => {})
      .finally(() => setStocksLoading(false));
  }, [etfFilter]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="border-b border-slate-800 bg-slate-900/40 px-4 lg:px-8 py-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="ew-title">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
              Early Warnings &amp; Safe Rotations
            </h1>
            <p className="text-xs text-slate-200 mt-1 max-w-3xl">
              4-layer scoring engine — market regime, subsector group health, early deterioration
              (D1-D18), and recovery/buy setups (R1-R10) — plus a Breakout Scanner (BK1-BK18).
              Daily EOD signals with optional 5-year backtest.
            </p>
          </div>
          <button onClick={load} disabled={refreshing}
                  data-testid="ew-refresh-btn"
                  className="text-xs px-4 py-2 rounded font-semibold border border-slate-500 hover:bg-slate-800 text-slate-100 flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <div className="px-4 lg:px-8 py-6 space-y-1">
        {error && (
          <div className="px-3 py-2 rounded bg-rose-900/30 border border-rose-600 text-xs text-rose-100 mb-3 font-semibold">
            {error}
          </div>
        )}

        <RegimeBanner regime={regime} loading={regimeLoading} />

        <h2 className="text-sm uppercase tracking-wider text-white font-bold mb-2 flex items-center gap-1.5">
          <Activity className="w-4 h-4 text-cyan-400" /> Subsector group health
        </h2>
        <SubsectorSection
          data={subsectors}
          loading={subLoading}
          onPick={setDrillTicker}
        />

        <h2 className="text-sm uppercase tracking-wider text-white font-bold mb-2 flex items-center gap-1.5">
          <Activity className="w-4 h-4 text-cyan-400" /> Stock-level scans
        </h2>
        <StockScreensSection
          data={stocks}
          loading={stocksLoading}
          onPick={setDrillTicker}
          etfFilter={etfFilter}
          setEtfFilter={setEtfFilter}
          etfOptions={etfOptions}
        />

        <h2 className="text-sm uppercase tracking-wider text-white font-bold mb-2 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-purple-300" /> Backtest enablement
        </h2>
        <BackfillPanel />
      </div>

      <StockDrillPanel ticker={drillTicker} onClose={() => setDrillTicker(null)} />
    </div>
  );
}
